/**
 * 邮件内容生成工具类
 */
import request from '../api/request.js'
import { formatFileSize, backToDownloadUrl } from './file.js'
import { createLogger } from './logger.js'
import { reportException } from './behaviorLog.js'
import { copyToClipboard } from './clipboard.js'

const logger = createLogger('Email')

/**
 * HTML 转义，防止 XSS
 * @param {string} text - 要转义的文本
 * @returns {string} 转义后的HTML
 */
export const escapeHtml = (text) => {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

const pad2 = (n) => String(n).padStart(2, '0')

/**
 * 将各种可能格式的有效期字符串格式化为：2025年03月28日 09:50
 * @param {string} expirationDate
 * @returns {string} 格式化后的日期（可能为 '永久' 或 '-'）
 */
export const formatExpirationForEmail = (expirationDate) => {
  const raw = (expirationDate ?? '').toString().trim()
  if (!raw || raw === '-') return '-'
  if (raw === '永久') return '永久'

  // 兼容：2025-03-28 09:50:00 / 2025-03-28 09:50 / 2025/03/28 09:50 等
  const m = raw.match(/^(\d{4})[-/年](\d{1,2})[-/月](\d{1,2})[日\sT]?(\d{1,2})?:?(\d{1,2})?.*$/)
  if (!m) return raw
  const [, y, mo, d, hh = '0', mm = '0'] = m
  return `${y}年${pad2(mo)}月${pad2(d)}日 ${pad2(hh)}:${pad2(mm)}`
}

/**
 * 生成 HTML 格式的邮件内容
 * @param {Array} uploadedFiles - 已上传的文件列表，格式：[{name, size, downloadUrl}, ...]
 * @param {string} shareLink - 分享链接
 * @param {string} expirationDate - 有效期（例如：2025-03-28 09:50:00 或 '永久'）
 * @returns {string} HTML格式的邮件内容
 */
export const generateEmailHTML = (uploadedFiles, shareLink, expirationDate = '-') => {
  const exp = formatExpirationForEmail(expirationDate)
  const expText = exp === '永久' ? '永久有效' : (exp === '-' ? '' : `${exp}失效`)

  // 更贴近邮件客户端的兼容写法：尽量用 table + inline style
  const filesHTML = uploadedFiles.map((file) => {
    const sizeText = formatFileSize(file.size)
    const metaText = expText ? `（${escapeHtml(sizeText)}, ${escapeHtml(expText)}）` : `（${escapeHtml(sizeText)}）`
    const downloadHref = file.downloadUrl ? escapeHtml(backToDownloadUrl(file.downloadUrl)) : ''

    return `
      <tr>
        <td style="padding: 14px 14px 0 14px;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
            <tr>
              <td width="44" valign="top" style="padding: 0 12px 14px 0;">
                <div style="width: 40px; height: 40px; background: #2f80ed; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #ffffff; font-size: 18px; line-height: 40px; text-align:center;">
                  <span style="display:inline-block;">📄</span>
                </div>
              </td>
              <td valign="top" style="padding: 0 0 14px 0;">
                <div style="font-size: 14px; color: #1f2d3d; font-weight: 600; line-height: 20px;">
                  ${escapeHtml(file.name)} <span style="font-size: 12px; color: #7a8aa0; font-weight: 400;">${metaText}</span>
                </div>
                ${downloadHref ? `
                  <div style="margin-top: 6px;">
                    <a href="${downloadHref}" style="font-size: 14px; color: #1d6fe8; text-decoration: underline;">点击下载</a>
                  </div>
                ` : ''}
              </td>
            </tr>
          </table>
          <div style="height: 1px; background: #e9eef6;"></div>
        </td>
      </tr>
    `
  }).join('')

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
    </head>
    <body>
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Microsoft YaHei', sans-serif; background: #ffffff; padding: 0; margin: 0;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
          <tr>
            <td align="left" style="padding: 0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse; border: 1px solid #d9e7f5;">
                <tr>
                  <td style="background: #eaf4ff; padding: 12px 14px; border-bottom: 1px solid #d9e7f5;">
                    <span style="font-size: 15px; font-weight: 700; color: #2b3a4a;">超大文件</span>
                  </td>
                </tr>
                ${filesHTML}
              </table>

              <div style="margin-top: 14px; font-size: 12px; color: #6b7c93; line-height: 18px;">
                <div style="font-weight: 700; color: #44566c; margin-bottom: 6px;">重要提醒</div>
                <div>1. 建议在收到邮件后尽快下载文件。</div>
                ${expText ? `<div>2. 文件/空间有效期：${escapeHtml(expText)}</div>` : ''}
                ${shareLink ? `<div>3. 分享链接（备用下载方式）：<a href="${escapeHtml(shareLink)}" style="color: #1d6fe8; text-decoration: underline;">${escapeHtml(shareLink)}</a></div>` : ''}
              </div>
            </td>
          </tr>
        </table>
      </div>
    </body>
    </html>
  `
}

/**
 * 生成纯文本格式的邮件内容
 * @param {Array} uploadedFiles - 已上传的文件列表，格式：[{name, size, downloadUrl}, ...]
 * @param {string} shareLink - 分享链接
 * @param {string} expirationDate - 有效期
 * @returns {string} 纯文本格式的邮件内容
 */
export const generateEmailText = (uploadedFiles, shareLink, expirationDate = '-') => {
  const exp = formatExpirationForEmail(expirationDate)
  const expText = exp === '永久' ? '永久有效' : (exp === '-' ? '' : `${exp}失效`)
  let text = '📎 超大文件\n'
  text += '='.repeat(30) + '\n\n'

  uploadedFiles.forEach((file, index) => {
    text += `文件 ${index + 1}：${file.name}\n`
    text += `文件大小：${formatFileSize(file.size)}\n`
    if (expText) text += `有效期：${expText}\n`
    if (file.downloadUrl) {
      const downloadUrl = backToDownloadUrl(file.downloadUrl)
      text += `下载链接：${downloadUrl}\n`
    }
    if (index < uploadedFiles.length - 1) {
      text += '\n' + '-'.repeat(30) + '\n\n'
    }
  })

  text += '\n' + '='.repeat(30) + '\n'
  text += '⚠️ 重要提醒：\n'
  text += '1. 建议在收到邮件后尽快下载文件。\n'
  if (expText) text += `2. 文件/空间有效期：${expText}\n`
  
  if (shareLink) {
    text += `${expText ? '3' : '2'}. 分享链接（备用下载方式）：\n`
    text += `${shareLink}\n`
  }

  return text
}

/**
 * 复制文件列表到邮件正文
 * @param {Array} uploadedFiles - 已上传的文件列表，格式：[{name, size, fileId}, ...]
 * @param {string} shareLink - 分享链接
 * @param {string} expirationDate - 有效期
 * @returns {Promise<{success: boolean, message: string}>} 复制结果
 */
export const copyToEmail = async (uploadedFiles, shareLink, expirationDate = '-') => {
  if (!uploadedFiles || uploadedFiles.length === 0) {
    return {
      success: false,
      message: '没有已上传的文件，请先上传文件'
    }
  }

  try {
    // 为每个文件获取下载链接（优先使用已保存的URL，避免重复请求）
    const filesWithDownloadUrl = await Promise.all(
      uploadedFiles.map(async (file) => {
        // 先检查是否已有保存的下载URL
        let downloadUrl = file.downloadUrl

        // 如果没有保存的URL，才请求获取
        if (!downloadUrl || downloadUrl === "") {
          downloadUrl = await request.getFileDownloadUrl(file)
          
          // 如果获取成功，保存到文件对象中，避免下次重复请求
          if (downloadUrl && downloadUrl !== "") {
            file.downloadUrl = downloadUrl
          }
        }

        return {
          name: file.name,
          size: file.size,
          downloadUrl: downloadUrl
        }
      })
    )

    // 生成 HTML 格式的邮件内容
    const htmlContent = generateEmailHTML(filesWithDownloadUrl, shareLink, expirationDate)

    // 生成纯文本格式作为备选
    const textContent = generateEmailText(filesWithDownloadUrl, shareLink, expirationDate)

    // 使用统一剪贴板复制方法（ClipboardItem → writeText → execCommand 三级降级）
    const success = await copyToClipboard(textContent, htmlContent)

    if (success) {
      return {
        success: true,
        message: '邮件正文已复制到剪贴板！\n\n请在邮件正文中粘贴使用。\n\n⚠️ 提醒：请在服务有效期内提示对方及时下载。'
      }
    }

    return {
      success: false,
      message: '复制失败，请重试。\n\n可尝试：\n1. 检查浏览器应用权限中是否已开启剪贴板权限。\n2. 更换浏览器打开专属链接重新点击复制。\n3. 第1，2种方法都无法解决可联系商家代发邮件。'
    }
  } catch (err) {
    logger.error('复制到邮件正文失败:', err)
    reportException(err, { action: 'copy_to_email_exception' })
    return {
      success: false,
      message: '复制失败，请重试。\n\n可尝试：\n1. 检查浏览器应用权限中是否已开启剪贴板权限。\n2. 更换浏览器打开专属链接重新点击复制。\n3. 第1，2种方法都无法解决可联系商家代发邮件。'
    }
  }
}

